import React, { useState } from 'react';
import { Button } from '@components/atoms/Button';
import { AlertItem } from '@hooks/api/warehouse/useLowStockAlerts';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { formatDateOnly } from '@utils/formatDate';

interface LowStockAlertCardProps {
  item: AlertItem;
  onOrder: (catalogId: string) => void;
  formatNumber: (value: number) => string;
}

export const LowStockAlertCard: React.FC<LowStockAlertCardProps> = ({
  item,
  onOrder,
  formatNumber,
}) => {
  const [expanded, setExpanded] = useState(false);
  const isCritical = item.isCritical;
  const colorPrefix = isCritical ? 'red' : 'amber';
  const available = Math.max(
    0,
    (item.stockLevel.quantityOnHand || 0) -
      (item.stockLevel.quantityReserved || 0),
  );
  const estimatedCost = item.suggestedOrder * (item.material.averageCost || 0);

  return (
    <div
      className={`bg-white rounded-lg shadow-sm ring-1 ring-${colorPrefix}-200 overflow-hidden`}>
      <div
        className={`bg-${colorPrefix}-50 border-l-4 border-${colorPrefix}-500 p-3 cursor-pointer`}
        onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 bg-${colorPrefix}-100 rounded-lg flex items-center justify-center`}>
              <Inventory2Icon
                className={`text-${colorPrefix}-600`}
                sx={{ fontSize: 20 }}
              />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                {item.material.name}
              </h3>
              <p className="text-xs text-gray-400">
                {item.material.sku || ''}{' '}
                {item.material.category?.name
                  ? `- ${item.material.category.name}`
                  : ''}
              </p>
            </div>
            <ExpandMoreIcon
              className="text-gray-400 transition-transform duration-200"
              sx={{
                fontSize: 20,
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            <div className="text-center">
              <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">
                On Hand
              </p>
              <p className={`text-base font-bold text-${colorPrefix}-600`}>
                {formatNumber(item.onHand)} {item.material.unit || ''}
              </p>
            </div>
            <div className="text-center">
              <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">
                Reorder Point
              </p>
              <p className="text-base font-semibold text-gray-900">
                {formatNumber(item.reorderPoint)} {item.material.unit || ''}
              </p>
            </div>
            <div className="text-center">
              <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">
                Suggested Order
              </p>
              <p className="text-base font-semibold text-gray-900">
                {formatNumber(item.suggestedOrder)} {item.material.unit || ''}
              </p>
            </div>
            <Button
              variant="primary"
              leftIcon={<AddShoppingCartIcon sx={{ fontSize: 16 }} />}
              onClick={(e) => {
                e.stopPropagation();
                onOrder(item.material.catalogId);
              }}
              className={
                isCritical
                  ? '!bg-red-600 hover:!bg-red-700'
                  : '!bg-amber-500 hover:!bg-amber-600'
              }>
              {isCritical ? 'Order Now' : 'Order'}
            </Button>
          </div>
        </div>

        {/* Progress Bar (warning items only) */}
        {!isCritical && (
          <div className="mt-3">
            <div className="flex items-center justify-between text-[11px] text-gray-400 mb-1">
              <span>Stock Level</span>
              <span>{item.percentOfReorder}% of reorder point</span>
            </div>
            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-amber-500 rounded-full transition-all"
                style={{ width: `${Math.min(item.percentOfReorder, 100)}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {/* Warehouse */}
          <div className="flex items-start gap-2">
            <WarehouseIcon
              className="text-gray-400 mt-0.5"
              sx={{ fontSize: 16 }}
            />
            <div>
              <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">
                Warehouse
              </p>
              <p className="text-sm font-medium text-gray-900">
                {item.stockLevel.warehouse?.name || '—'}
              </p>
            </div>
          </div>

          {/* Reserved / Available */}
          <div>
            <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">
              Reserved
            </p>
            <p className="text-sm font-semibold text-gray-900">
              {formatNumber(item.stockLevel.quantityReserved || 0)}{' '}
              {item.material.unit || ''}
            </p>
          </div>
          <div>
            <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">
              Available
            </p>
            <p
              className={`text-sm font-semibold ${
                available <= 0 ? 'text-red-600' : 'text-green-600'
              }`}>
              {formatNumber(available)} {item.material.unit || ''}
            </p>
          </div>

          {/* Avg Cost */}
          <div className="flex items-start gap-2">
            <AttachMoneyIcon
              className="text-gray-400 mt-0.5"
              sx={{ fontSize: 16 }}
            />
            <div>
              <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">
                Avg Cost
              </p>
              <p className="text-sm font-medium text-gray-900">
                ${(item.material.averageCost || 0).toFixed(2)} /{' '}
                {item.material.unit || 'unit'}
              </p>
            </div>
          </div>

          {/* Estimated Reorder Cost */}
          <div>
            <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">
              Est. Reorder Cost
            </p>
            <p className="text-sm font-bold text-gray-900">
              ${estimatedCost.toFixed(2)}
            </p>
          </div>

          {/* Default Supplier */}
          <div className="flex items-start gap-2">
            <LocalShippingIcon
              className="text-gray-400 mt-0.5"
              sx={{ fontSize: 16 }}
            />
            <div>
              <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">
                Supplier
              </p>
              <p className="text-sm font-medium text-gray-900">
                {item.material.defaultSupplier || '—'}
              </p>
            </div>
          </div>

          {/* Lead Time */}
          <div>
            <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">
              Lead Time
            </p>
            <p className="text-sm font-medium text-gray-900">
              {item.material.leadTimeDays
                ? `${item.material.leadTimeDays} days`
                : '—'}
            </p>
          </div>

          {/* Last Received */}
          <div className="flex items-start gap-2">
            <CalendarTodayIcon
              className="text-gray-400 mt-0.5"
              sx={{ fontSize: 16 }}
            />
            <div>
              <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">
                Last Received
              </p>
              <p className="text-sm font-medium text-gray-900">
                {item.stockLevel.lastReceivedAt
                  ? formatDateOnly(item.stockLevel.lastReceivedAt)
                  : '—'}
              </p>
            </div>
          </div>

          {/* Last Consumed */}
          <div>
            <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">
              Last Consumed
            </p>
            <p className="text-sm font-medium text-gray-900">
              {item.stockLevel.lastConsumedAt
                ? formatDateOnly(item.stockLevel.lastConsumedAt)
                : '—'}
            </p>
          </div>

          {/* Description */}
          {item.material.description && (
            <div className="col-span-2 sm:col-span-3 lg:col-span-4">
              <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">
                Description
              </p>
              <p className="text-sm text-gray-700">
                {item.material.description}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
